import React, { useEffect, useState, useCallback } from 'react';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import MaxWidth from '../../../../Layout/MaxWidth';
import { theme } from '../../../../Theme';
import { BaseHeading } from '../../../../ui/Typography';
import JournalCard from '../../bloks/JournalCard';
import JournalCategories from '../../bloks/JournalCategories';
import { useLocation, useHistory } from 'react-router-dom';

const JournalPageWrapper = css`
  margin-bottom: 5rem;
  ${theme.below.md} {
    margin-bottom: 2rem;
  }

  h1 {
    ${BaseHeading};
    font-size: 2.25rem;
    text-align: center;
    margin: 3.5rem auto;
    text-transform: none;
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: 0.002em;

    ${theme.below.md} {
      font-size: 1.5rem;
    }
  }
`;

const CustomWidth = styled(MaxWidth)`
  max-width: 880px;
`;

const CardsWrapper = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1rem;

  & > * {
    width: 100%;
  }

  ${theme.above.sm} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${theme.above.md} {
    grid-template-columns: repeat(3, 1fr);
  }

  .two-column {
    ${theme.above.sm} {
      grid-column: span 2;
    }
  }
`;

const CategoryTitlesWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0 0 60px 0;
  column-gap: 1rem;
  row-gap: 0.2em;
  transition: row-gap 0.3s ease;

  ${theme.above.sm} {
    row-gap: 0.5rem;
  }

  span {
    cursor: pointer;
    font-size: 0.9rem;
    color: inherit;

    &:hover,
    &.active {
      text-decoration: underline;
    }
  }
`;

const formatCategoryForUrl = category => {
  return category ? category.toLowerCase().replace(/\s+/g, '-') : '';
};

const JournalOverview = ({
  page_title,
  articles,
  journal_categories,
  all_articles_label
}) => {
  const [selectedCategory, setSelectedCategory] = useState(all_articles_label);
  const location = useLocation();
  const history = useHistory();

  const categoriesWithAllArticles = [
    { title: all_articles_label, _uid: 'all-articles' },
    ...(journal_categories?.[0]?.category || [])
  ];

  const getCategoryFromUrl = urlCategory => {
    const foundCategory = categoriesWithAllArticles.find(
      category => formatCategoryForUrl(category.title) === urlCategory
    );
    return foundCategory ? foundCategory.title : all_articles_label;
  };

  useEffect(() => {
    const categoryParam = new URLSearchParams(location.search).get('c');
    if (categoryParam) {
      const category = getCategoryFromUrl(categoryParam);
      setSelectedCategory(category);
    } else {
      setSelectedCategory(all_articles_label);
    }
  }, [location.search, all_articles_label]);

  useEffect(() => {
    const categoryParam = new URLSearchParams(location.search).get('c');
    if (!categoryParam) {
      setSelectedCategory(all_articles_label);
    }
  }, [location.pathname]);

  const filteredArticles =
    selectedCategory === all_articles_label
      ? articles
      : articles.filter(article =>
          article.content.journal_categories?.some(
            categoryObj => categoryObj.category?.[0]?.title === selectedCategory
          )
        );

  const handleCategorySelect = useCallback(
    category => {
      if (category !== selectedCategory) {
        setSelectedCategory(category);

        const params = new URLSearchParams(location.search);
        const newCategory = formatCategoryForUrl(category);

        if (category === all_articles_label) {
          params.delete('c');
        } else {
          params.set('c', newCategory);
        }

        history.replace({ search: params.toString() });
      }
    },
    [selectedCategory, history, location.search, all_articles_label]
  );

  return (
    <div className={JournalPageWrapper}>
      <MaxWidth>{page_title && <h1>{page_title}</h1>}</MaxWidth>

      <MaxWidth>
        <CategoryTitlesWrapper>
          {categoriesWithAllArticles.map(category => (
            <span
              key={category._uid}
              onClick={e => {
                e.preventDefault();
                handleCategorySelect(category.title);
              }}
              className={selectedCategory === category.title ? 'active' : ''}
            >
              {category.title}
            </span>
          ))}
        </CategoryTitlesWrapper>
      </MaxWidth>

      <JournalCategories
        categories={journal_categories}
        onCategorySelect={handleCategorySelect}
      />

      {filteredArticles.length > 0 && (
        <MaxWidth>
          <div className={CardsWrapper}>
            {filteredArticles.map(article => (
              <div
                key={article.id}
                className={article.content?.ratio === '5:4' ? 'two-column' : ''}
              >
                <JournalCard {...article} minimalLayout={true} />
              </div>
            ))}
          </div>
        </MaxWidth>
      )}
    </div>
  );
};

export default JournalOverview;
